/**
 * The platform's own sites, for the desk's card and the add dialog.
 *
 * Read once per mount and again after a site was added (`reload`), because the
 * list marks which sites are already watched. Only asked for somebody who may
 * add sites: the route answers to `manage:crm.monitoring`. A failed read leaves
 * `null`, and both screens simply do not offer the platform sites.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { listPlatformSites, type PlatformSiteOption } from "../../../../lib/monitoringPlatform";

export function usePlatformSites(enabled: boolean): { sites: PlatformSiteOption[] | null; reload: () => void } {
  const [sites, setSites] = useState<PlatformSiteOption[] | null>(null);
  const requestId = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!enabled) return;
    const rid = ++requestId.current;
    listPlatformSites()
      .then((res) => {
        if (alive.current && rid === requestId.current) setSites(res.sites);
      })
      .catch(() => {
        // Left as it was: the card and the dialog fall back to a plain address.
      });
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { sites, reload: load };
}
